import {
    AngleSidebar,
    BackLink,
    Hero,
    PageContent,
    RichText,
} from "@components";
import { getMetaData } from "@components/PageHead";
import {
    IConfigEmailTemplate,
    IPage,
    IPageContentAngle,
    PageContext,
} from "@types";
import { componentMapper, isFirstSection } from "@utils/componentMapper";
import { graphql } from "gatsby";
import React from "react";

interface Props extends PageContext {
    data: {
        page: IPage;
        emailTemplate: IConfigEmailTemplate;
    };
}

const Angle = ({ data: { page, emailTemplate } }: Props) => {
    const { pageContent: untypedContent, pageSeo } = page;
    const pageContent = untypedContent as unknown as IPageContentAngle;
    const backLinkSlug = pageContent?.category?.[0].angleHomepage?.slug;
    const backLink = backLinkSlug && (
        <BackLink destinationSlug={backLinkSlug} pageType="angle" />
    );

    const introText = pageContent.introText && (
        <RichText
            options={{ family: "stix", color: "main" }}
            textNode={pageContent.introText}
        />
    );

    const sidebarProps = {
        metaData: getMetaData({ title: page.title }, pageSeo),
        emailTemplate,
        pageContent,
        backlinkPresent: !!backLink,
        whitepaperUrl: pageContent.whitepaperUrl,
    };

    return (
        <>
            {pageContent && (
                <PageContent>
                    <Hero
                        categories={pageContent?.category}
                        data={pageContent?.hero}
                        landingPage={page.slug === "home"}
                    />
                    {pageContent?.content?.map((item, idx) => {
                        if (!isFirstSection(item, idx))
                            return componentMapper(item, undefined, idx);

                        return componentMapper(
                            {
                                ...item,
                                backLink,
                                introText,
                                sidebar: <AngleSidebar {...sidebarProps} />,
                            },
                            undefined,
                            idx,
                        );
                    })}
                </PageContent>
            )}
        </>
    );
};

export default Angle;

export const query = graphql`
    query ($id: String!, $language: String!, $investor: String) {
        emailTemplate: contentfulConfigEmailTemplate(
            usedFor: { eq: "Angle sharing" }
            node_locale: { eq: $language }
        ) {
            body {
                body
            }
            subject
        }
        page: contentfulComposePage(id: { eq: $id }) {
            id
            slug
            pageTheme
            title
            locales
            pageContent(investor: $investor) {
                ... on ContentfulPageContentAngle {
                    hero {
                        ...Hero
                    }
                    content {
                        ...GeneralContent
                    }
                    authors {
                        ...Author
                    }
                    introText {
                        raw
                    }
                    shortTitle {
                        raw
                    }
                    publishDate
                    createdAt
                    whitepaperUrl
                    updatedAt
                    category {
                        mainCategory
                        subCategory
                        angleHomepage {
                            slug
                        }
                    }
                }
            }
            pageSeo(investor: $investor) {
                ...SEO
            }
            disablePageForInvestorTypes
            fallbackPage {
                ... on ContentfulComposePage {
                    slug
                }
            }
        }
    }
`;
